import { Injectable } from '@angular/core';
const MIME_TYPE = 'image/jpeg';
import { SessionStore } from '../stores/session.store';

@Injectable({
  providedIn: 'root'
})
export class ImageCacheService {
  private cacheName: string = 'images';

  constructor() { }

  public async put(name, base64) {
    const cache = await caches.open(this.cacheName);
    const blob = await fetch(base64).then(res => res.blob());
    const resp = new Response(
        blob, {
            status: 200,
            statusText: 'ok',
            headers: {
                'content-type': MIME_TYPE,
                'content-length': `${blob.size}`
            }
        }
    )
    const path = `/images/base64/${name}`;
    await cache.put(path, resp);

    return path;
  }

  public async get(name) {
    const cache = await caches.open(this.cacheName);
    return cache.match(name).then(cachedResponse => {
      return cachedResponse;
    });
  }

  public async getBase64(photo, compress = false) {
    const response = await this.get(photo);
    let buffer = await response.blob();
    if (compress) {
      buffer = await this.compress(buffer);
    }
    const base64 = await this.blobToBase64(buffer)

    return base64;
  }

  public async getBlob(photo, compress = false) {
    const response = await this.get(photo);
    let buffer = await response.blob();
    if (compress) {
      buffer = await this.compress(buffer);
    }

    return buffer;
  }

  blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  getImage(blob): Promise<HTMLImageElement> {
    return new Promise((resolve, _) => {
      const blobURL = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = ()  => {
        resolve(img);
      };
      img.src = blobURL;
    })
  }

  async compress(blob): Promise<Blob> {
    const image = await this.getImage(blob);
    const canvas = document.createElement('canvas');
    const { width, height } = image;
    const ctx = canvas.getContext('2d');

    ctx.canvas.width  = width;
    ctx.canvas.height = height;

    ctx.drawImage(image, 0, 0, width, height, 0, 0, width, height);
    return new Promise((resolve, _) => {
      canvas.toBlob( (blob) => resolve(blob), MIME_TYPE, 0.3);
    });
  }

  async getStatus() {
    const cache = await caches.open(this.cacheName);
    const keys = await cache.keys();

    const promises = keys.map(async ( name) => {
      const data = await cache.match(name);
      const blob = await data?.blob();
      return {
        name: name.url,
        size: blob?.size || 0
      };
    });

    const data = await Promise.all(promises);
    const size = data.reduce((total, { size }) => total+=size, 0);

    return  {
      size,
      data
    }
  }

  async prune(remove = false) {
    const luminaries = Object.keys(localStorage).filter((key) => key.indexOf('luminaries') >=0).map((key) => JSON.parse(localStorage.getItem(key))?.value || []).flat();
    const maintenances = Object.keys(localStorage).filter((key) => key.indexOf('assignedMaintenances') >=0).map((key) => JSON.parse(localStorage.getItem(key))?.value || []).flat();
    const flattenedMaintenanesPhotos = maintenances.map(({photos}) => [...(photos?.initial || []), ...(photos?.final || [])]).flat().map((name) => name.split('/').pop()) ;
    const expansions = Object.keys(localStorage).filter((key) => key.indexOf('unsync-expansions') >=0).map((key) => JSON.parse(localStorage.getItem(key))?.value || []).flat();
    const flattenedExpansionsPhotos = expansions.map(({photos}) => [...(photos?.initial || []), ...(photos?.final || [])]).flat().map((name) => name.split('/').pop()) ;
    const modernizations = Object.keys(localStorage).filter((key) => key.indexOf('unsync-modernizations') >=0).map((key) => JSON.parse(localStorage.getItem(key))?.value || []).flat();
    const flattenedModernizationsPhotos = modernizations.map(({photos}) => [...(photos?.initial || []), ...(photos?.final || [])]).flat().map((name) => name.split('/').pop()) ;
    const flattenedLuminaryPhotos = luminaries.map(({photos}) => photos || []).flat().map((name) => name.split('/').pop()) ;
    const flattenedPhotos = [...flattenedMaintenanesPhotos, ...flattenedLuminaryPhotos, ...flattenedModernizationsPhotos, ...flattenedExpansionsPhotos];
    const cache = await caches.open(this.cacheName);
    const keys = await cache.keys();
    let count = 0;
    const promises = keys.map(async ( name) => {
      const photoName = name.url.split('/').pop()

      if (flattenedPhotos.indexOf(photoName) >= 0) {
        return;
      }
      count++;
      if (remove) {
        await cache.delete(name);
      }
    });

    await Promise.all(promises);

    return count;
  }

}
